import { creatorbase } from '@round/api';
import { Card, SelectOption, Skeleton, tableHelpers } from '@round/creatorbase-ui';
import { ColumnDef } from '@tanstack/react-table';
import { asMoneyWithoutZeros, formatDateShort, numberWithCommas } from 'helpers';
import usePreviousCampaigns, { PreviousCampaignsData } from './usePreviousCampaigns';
import styles from './PreviousCampaigns.module.css';
import { buildCampaignUrl, buildProjectUrl } from 'Modules/Plans/helpers';
import { getTableMetaHelper } from '@round/ui-kit';
import VirtualizedTable from '../../components/VirtualizedTable/VirtualizedTable';
import TableSortingSelect from '../../components/TableSortingSelect/TableSortingSelect';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import cn from 'classnames';
import { useState } from 'react';
import TableEmptyState from '../../components/TableEmptyState/TableEmptyState';

const { SkeletonCell, TruncatedLinkCell, TruncatedCell } = tableHelpers;

function formatMetric(value: unknown): string {
    return typeof value === 'number' ? numberWithCommas(value) : '-';
}

type PlatformDetails =
    | creatorbase.InstagramPostDetails
    | creatorbase.TiktokPostDetails
    | creatorbase.YoutubePostDetails;

function getPostPlatformDetails(row: tableHelpers.WithLoadingRow<PreviousCampaignsData>): PlatformDetails | null {
    return tableHelpers.isLoadingRow(row) ? null : row[`${row.platform}_details`];
}

type Meta = { isLoading?: boolean; isError: boolean };
const getTableMeta = getTableMetaHelper<Meta>();

type PostsOrdering = NonNullable<creatorbase.GetPostsParams['ordering']>;

const sortingOptions: SelectOption<PostsOrdering>[] = [
    { label: 'Most recent', value: '-published_date' },
    { label: 'Most views', value: '-view_count' },
    { label: 'Highest engagement', value: '-engagement_rate' },
    { label: 'Most likes', value: '-like_count' },
    { label: 'Most comments', value: '-comment_count' },
    { label: 'Most shares', value: '-share_count' },
    { label: 'Most saves', value: '-save_count' },
];

type Props = {
    account: creatorbase.ClientInfluencerAccount | undefined | null;
    isLoading: boolean;
};

const PreviousCampaigns = ({ account }: Props) => {
    const [sorting, setSorting] = useState<PostsOrdering>(sortingOptions[0].value);
    const { posts, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, isError } = usePreviousCampaigns(
        account,
        sorting
    );
    const { getCurrencyById } = useCurrencies();

    const columns: ColumnDef<tableHelpers.WithLoadingRow<PreviousCampaignsData>, any>[] = [
        {
            header: 'Project',
            id: 'project',
            meta: { className: styles.projectCell },
            accessorFn: (row) => ({
                href: buildProjectUrl(row.campaign?.project_id),
                value: row.project_name ?? '-',
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Brand',
            id: 'brand',
            meta: { className: cn(styles.brandCell, styles.fadedCell) },
            accessorFn: (row) => row.brand_name ?? '-',
            cell: TruncatedCell,
        },
        {
            header: 'Campaign Name',
            id: 'campaignName',
            meta: { className: styles.campaignCell },
            accessorFn: (row) => ({
                href: buildCampaignUrl(row.campaign?.project_id, row.campaign),
                value: row.campaign?.name ?? '-',
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Post URL',
            accessorKey: 'post_url',
            meta: { className: styles.postCell },
            cell: ({ row: { original }, table }) => {
                const { isLoading } = getTableMeta(table);
                if (isLoading) {
                    return <Skeleton className={styles.skeleton} />;
                }

                return (
                    <a href={original.post_url} className={styles.postUrl} target="_blank" rel="noopener noreferrer">
                        {original.post_url}
                    </a>
                );
            },
        },
        {
            header: 'Upload date',
            id: 'uploadDate',
            meta: { className: styles.fadedCell },
            accessorFn: (row) => (typeof row.upload_date === 'string' ? formatDateShort(row.upload_date) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Cost',
            id: 'cost',
            meta: { className: styles.fadedCell },
            accessorFn: (row) => asMoneyWithoutZeros(row.cost, getCurrencyById(row.currency_id)),
            cell: SkeletonCell,
        },
        {
            header: 'Views',
            id: 'views',
            accessorFn: (row) => formatMetric(row.view_count),
            cell: SkeletonCell,
        },
        {
            header: 'Likes',
            id: 'likes',
            accessorFn: (row) => formatMetric(getPostPlatformDetails(row)?.like_count),
            cell: SkeletonCell,
        },
        {
            header: 'Comments',
            id: 'comments',
            accessorFn: (row) => formatMetric(getPostPlatformDetails(row)?.comment_count),
            cell: SkeletonCell,
        },
        {
            header: 'Shares',
            id: 'shares',
            accessorFn: (row) => {
                const platformDetails = getPostPlatformDetails(row);
                if (!platformDetails || !('share_count' in platformDetails)) {
                    return '-';
                }

                return formatMetric(platformDetails.share_count);
            },
            cell: SkeletonCell,
        },
        {
            header: 'Saves',
            id: 'saves',
            accessorFn: (row) => {
                const platformDetails = getPostPlatformDetails(row);
                if (!platformDetails || !('save_count' in platformDetails)) {
                    return '-';
                }

                return formatMetric(platformDetails.save_count);
            },
            cell: SkeletonCell,
        },
    ];

    const meta: Meta = { isLoading, isError };
    const showSavesColumn = posts.some((p) => p.platform === 'tiktok');
    const tableRows = tableHelpers.getLoadingRows(posts, { pageSize: 25, isLoading });

    const noDataTitle = meta.isError ? 'Something went wrong' : 'No previous campaigns found';
    const noDataDescription = meta.isError
        ? 'Failed to load previous campaigns. Please try again.'
        : 'No previous campaigns found for this creator. Please try again later.';

    return (
        <Card size="sm" variant="borderless" className={styles.cardContainer}>
            <Card.Header className={cn(styles.cardHeader, styles.header)}>
                <Card.Title>Previous Campaigns</Card.Title>
                <TableSortingSelect
                    options={sortingOptions.filter((option) => showSavesColumn || option.value !== '-save_count')}
                    value={sorting}
                    onChange={setSorting}
                />
            </Card.Header>
            <Card.Content className={styles.cardContent}>
                <VirtualizedTable
                    data={tableRows}
                    meta={meta}
                    columns={columns}
                    enableHiding
                    columnVisibility={{ saves: showSavesColumn }}
                    onEndReached={() => {
                        if (isLoading || !hasNextPage || isFetchingNextPage) {
                            return;
                        }

                        void fetchNextPage();
                    }}
                    virtualizerOptions={{
                        count: tableRows.length,
                        estimateSize: () => 50,
                        getItemKey: (index) => `${index}-${posts[index]?.id}` || `skeleton-${index}`,
                    }}
                    noDataLabel={
                        <TableEmptyState isError={meta.isError} title={noDataTitle} description={noDataDescription} />
                    }
                />
            </Card.Content>
        </Card>
    );
};

export default PreviousCampaigns;
